import React, {useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Field, getIn} from 'formik';
import {Select} from 'formik-material-ui';
import FormControl from '../components/FormControl.jsx';
import InputLabel from '../components/InputLabel.jsx';

import styles from '../styles/styles.js';
import useFormikSchema from '../hooks/useFormikSchema';
import useUniqueId from '../hooks/useUniqueId.js';
import FormHelperText from '../components/FormHelperText.jsx';
import formatError, {formatErrorWithIcon} from '../utils/formatError.jsx';

const {makeStyles} = styles;

const SelectComponent = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const activeId = useUniqueId();
  const displayProps = useMemo(() => {
    if (isExpanded) {
      return {
        'aria-expanded': isExpanded,
        'aria-activedescendant': activeId,
      };
    }
    return {
      'aria-expanded': isExpanded,
    };
  }, [isExpanded]);

  return (
    <Select
      {...props}
      SelectDisplayProps={displayProps}
      MenuProps={{
        MenuListProps: {
          id: activeId,
        },
      }}
      onOpen={()=>setIsExpanded(true)}
      onClose={()=>setIsExpanded(false)}
    />
  );
};

const FormikSelect = (props) => {
  const classes = useStyles();
  const uniqueId = useUniqueId();
  const {disabled, value, name, label, gridProps, errors, touched, loading, schema, ...rest} = props;
  const dataTestId = props['data-testid'];
  const {formattedLabel} = useFormikSchema(name, label, schema);
  const fieldError = getIn(errors, name);
  const showError = getIn(touched, name) && !!fieldError;

  return (
    <FormControl variant={'outlined'} fullWidth error={showError && fieldError}>
      <InputLabel
        variant={'outlined'}
        id={uniqueId}
        error={showError}>
        {formattedLabel}
      </InputLabel>
      <Field
        data-testid={dataTestId}
        {...rest}
        fullWidth
        value={value}
        disabled={disabled}
        variant={'outlined'}
        label={formattedLabel}
        name={name}
        labelId={uniqueId}
        className={classes.select}
        component={SelectComponent}>
        { props.children }
      </Field>
      {
        showError && (
          <FormHelperText role="alert" aria-label={formatError(fieldError)}>
            {formatErrorWithIcon(fieldError)}
          </FormHelperText>
        )
      }
    </FormControl>
  );
};

FormikSelect.propTypes = {
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  errors: PropTypes.object,
  touched: PropTypes.object,
};

const useStyles = makeStyles((theme) => ({
  select: {
    textAlign: 'left',
  },
}));

export default FormikSelect;
